import React from "react";
import { Box, Paper, Skeleton } from "@mui/material";

// ─── reusable shimmer line ────────────────────────────────────────────────────

interface ShimmerLineProps {
  width?: string | number;
  height?: number;
  sx?: object;
}

const ShimmerLine: React.FC<ShimmerLineProps> = ({
  width = "100%",
  height = 16,
  sx = {},
}) => (
  <Skeleton
    variant="rectangular"
    width={width}
    height={height}
    sx={{ borderRadius: 1, ...sx }}
    animation="wave"
  />
);

// ─── reusable shimmer text field ─────────────────────────────────────────────
// Mimics MUI outlined TextField with a floating label

const ShimmerTextField: React.FC<{ sx?: object }> = ({ sx = {} }) => (
  <Box sx={{ position: "relative", ...sx }}>
    {/* Floating label */}
    <Skeleton
      variant="rectangular"
      width={120}
      height={12}
      animation="wave"
      sx={{
        position: "absolute",
        top: -6,
        left: 12,
        zIndex: 1,
        borderRadius: 1,
        bgcolor: "grey.200",
      }}
    />
    {/* Input box */}
    <Skeleton
      variant="rectangular"
      width="100%"
      height={52}
      animation="wave"
      sx={{ borderRadius: 2, bgcolor: "grey.100" }}
    />
  </Box>
);

// ─── shimmer toggle button ────────────────────────────────────────────────────

const ShimmerToggle: React.FC<{ width?: number }> = ({ width = 120 }) => (
  <Skeleton
    variant="rectangular"
    width={width}
    height={42}
    animation="wave"
    sx={{ borderRadius: 2, bgcolor: "grey.100" }}
  />
);

// ─── shimmer checkbox row ─────────────────────────────────────────────────────

const ShimmerCheckboxRow: React.FC<{ lines?: number }> = ({ lines = 1 }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
    {/* Checkbox square */}
    <Skeleton
      variant="rectangular"
      width={18}
      height={18}
      animation="wave"
      sx={{ borderRadius: 0.5, flexShrink: 0, mt: 0.25 }}
    />
    {/* Label text lines */}
    <Box sx={{ flex: 1 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          width={i === lines - 1 ? "60%" : "100%"}
          height={12}
          animation="wave"
          sx={{ borderRadius: 1, mb: i < lines - 1 ? 0.75 : 0 }}
        />
      ))}
    </Box>
  </Box>
);

// ─── main shimmer component ───────────────────────────────────────────────────

const Step1BasicInfoSkeleton: React.FC = () => (
  <Box>
    {/* ── Section header ── */}
    <Box sx={{ mb: 4 }}>
      {/* "✦ Basic Info" breadcrumb */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Skeleton variant="rectangular" width={13} height={13} animation="wave" sx={{ borderRadius: 0.5 }} />
        <Skeleton variant="rectangular" width={70} height={13} animation="wave" sx={{ borderRadius: 1 }} />
      </Box>

      {/* "What can we help you with today?" — h3 */}
      <Skeleton
        variant="rectangular"
        width="68%"
        height={38}
        animation="wave"
        sx={{ borderRadius: 1.5, mb: 1.5 }}
      />

      {/* Subtitle — two lines */}
      <Skeleton variant="rectangular" width="78%" height={14} animation="wave" sx={{ borderRadius: 1, mb: 0.75 }} />
      <Skeleton variant="rectangular" width="55%" height={14} animation="wave" sx={{ borderRadius: 1 }} />
    </Box>

    {/* ── Form card ── */}
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        bgcolor: "#FAFCFB",
      }}
    >
      {/* ── Select Product heading ── */}
      <Skeleton
        variant="rectangular"
        width={140}
        height={22}
        animation="wave"
        sx={{ borderRadius: 1, mb: 2.5 }}
      />

      {/* ── Product card (single wide card in screenshot) ── */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2.5,
          border: "2px solid",
          borderColor: "grey.200",
          bgcolor: "grey.50",
          width: "100%",
          maxWidth: 640,
        }}
      >
        {/* Icon circle */}
        <Skeleton
          variant="circular"
          width={52}
          height={52}
          animation="wave"
          sx={{ mb: 2, bgcolor: "grey.200" }}
        />
        {/* Product name */}
        <Skeleton variant="rectangular" width="55%" height={18} animation="wave" sx={{ borderRadius: 1, mb: 1 }} />
        {/* Description line 1 */}
        <Skeleton variant="rectangular" width="80%" height={13} animation="wave" sx={{ borderRadius: 1, mb: 0.75 }} />
        {/* Description line 2 */}
        <Skeleton variant="rectangular" width="60%" height={13} animation="wave" sx={{ borderRadius: 1, mb: 2 }} />
        {/* Badge chip */}
        <Skeleton variant="rectangular" width={130} height={22} animation="wave" sx={{ borderRadius: 3 }} />
      </Paper>

      {/* ── "Who is applying" heading ── */}
      <Skeleton
        variant="rectangular"
        width={280}
        height={22}
        animation="wave"
        sx={{ borderRadius: 1, mb: 2 }}
      />

      {/* ── Applicant toggle buttons ── */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 5 }}>
        <ShimmerToggle width={120} />
        <ShimmerToggle width={148} />
      </Box>

      {/* ── First Name / Last Name row ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2.5,
          mb: 4,
        }}
      >
        <ShimmerTextField />
        <ShimmerTextField />
      </Box>

      {/* ── Mobile Number / Email Id row ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2.5,
          mb: 4,
        }}
      >
        {/* Phone field — has endAdornment button */}
        <Box sx={{ position: "relative" }}>
          <Skeleton
            variant="rectangular"
            width={140}
            height={12}
            animation="wave"
            sx={{ position: "absolute", top: -6, left: 12, zIndex: 1, borderRadius: 1, bgcolor: "grey.200" }}
          />
          <Box sx={{ position: "relative" }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={52}
              animation="wave"
              sx={{ borderRadius: 2, bgcolor: "grey.100" }}
            />
            {/* "Get OTP" button shimmer inside field */}
            <Skeleton
              variant="rectangular"
              width={80}
              height={30}
              animation="wave"
              sx={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                borderRadius: 1.5,
                bgcolor: "grey.200",
              }}
            />
          </Box>
        </Box>

        {/* Email field — has "Verify" endAdornment */}
        <Box sx={{ position: "relative" }}>
          <Skeleton
            variant="rectangular"
            width={120}
            height={12}
            animation="wave"
            sx={{ position: "absolute", top: -6, left: 12, zIndex: 1, borderRadius: 1, bgcolor: "grey.200" }}
          />
          <Box sx={{ position: "relative" }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={52}
              animation="wave"
              sx={{ borderRadius: 2, bgcolor: "grey.100" }}
            />
            {/* "Verify" button shimmer inside field */}
            <Skeleton
              variant="rectangular"
              width={68}
              height={30}
              animation="wave"
              sx={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                borderRadius: 1.5,
                bgcolor: "grey.200",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Pincode — half width ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2.5,
          mb: 5,
        }}
      >
        <ShimmerTextField />
      </Box>

      {/* ── Consent checkboxes ── */}
      <Box sx={{ mb: 3 }}>
        {/* Terms & Conditions — 1 line */}
        <ShimmerCheckboxRow lines={1} />
        {/* Privacy Policy — 3 lines (long text) */}
        <ShimmerCheckboxRow lines={3} />
      </Box>

      {/* ── Continue button — bottom right ── */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Skeleton
          variant="rectangular"
          width={120}
          height={46}
          animation="wave"
          sx={{ borderRadius: 2, bgcolor: "grey.300" }}
        />
      </Box>
    </Paper>
  </Box>
);

export default Step1BasicInfoSkeleton;